import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getCompanyApi, getServiceApi, getSourcesApi } from './company';
import { getStatusApi } from './threats';
import {
  CompanyLookupItem,
  ServiceLookupItem,
  SourceLookupItem,
  ThreatStatusLookupItem,
} from '../types/LookupsTypes';

/**
 * Reference/lookup data shared across pages (WHEEL-336). These rarely change,
 * so a longer staleTime avoids the refetch-on-every-mount behaviour the
 * former Redux sagas had while still keeping data reasonably fresh.
 */
const LOOKUP_STALE_TIME = 5 * 60_000;

export const companiesQueryKey = ['lookups', 'companies'];

/** Companies list - reused by nav/search, filters and create-threat flows. */
export const useCompaniesQuery = (
  enabled = true,
): UseQueryResult<CompanyLookupItem[]> =>
  useQuery<CompanyLookupItem[]>({
    queryKey: companiesQueryKey,
    queryFn: async () => {
      const response = await getCompanyApi('');
      return response?.data ?? [];
    },
    staleTime: LOOKUP_STALE_TIME,
    enabled,
  });

/** Threat statuses - endpoint responds `{ items: [...] }`, unwrapped here. */
export const useThreatStatusesQuery = (
  enabled = true,
): UseQueryResult<ThreatStatusLookupItem[]> =>
  useQuery<ThreatStatusLookupItem[]>({
    queryKey: ['lookups', 'threatStatuses'],
    queryFn: async () => {
      const response = await getStatusApi('');
      return response?.data?.items ?? [];
    },
    staleTime: LOOKUP_STALE_TIME,
    enabled,
  });

/** Services - endpoint responds `{ items: [...] }`, unwrapped here. */
export const useServicesQuery = (
  enabled = true,
): UseQueryResult<ServiceLookupItem[]> =>
  useQuery<ServiceLookupItem[]>({
    queryKey: ['lookups', 'services'],
    queryFn: async () => {
      const response = await getServiceApi('');
      return response?.data?.items ?? [];
    },
    staleTime: LOOKUP_STALE_TIME,
    enabled,
  });

/** Threat sources - endpoint responds `{ items: [...] }`, unwrapped here. */
export const useSourcesQuery = (
  enabled = true,
): UseQueryResult<SourceLookupItem[]> =>
  useQuery<SourceLookupItem[]>({
    queryKey: ['lookups', 'sources'],
    queryFn: async () => {
      const response = await getSourcesApi('');
      return response?.data?.items ?? [];
    },
    staleTime: LOOKUP_STALE_TIME,
    enabled,
  });
